import React from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { event as gaEvent } from '../../utils/analytics';

const Intakes: React.FC = () => {
  const intakes = [
    {
      month: "January",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      description: "Winter intake for language schools"
    },
    {
      month: "April",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      description: "Spring intake for language schools"
    },
    {
      month: "July",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      description: "Summer intake for language schools"
    },
    {
      month: "October",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      description: "Fall intake for language schools"
    }
  ];

  const handleApplyClick = (month: string) => {
    // Track click on Apply Now button for specific intake
    gaEvent({
      action: 'apply_button_click',
      category: 'Intake',
      label: `${month} Intake`
    });
  };

  return (
    <section className="py-16 bg-slate-50 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div 
          className="absolute -bottom-40 -right-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl"
          animate={{ 
            x: [0, 30],
            y: [0, -30]
          }}
          transition={{ 
            repeat: Infinity,
            repeatType: "reverse",
            duration: 20,
            ease: "easeInOut" 
          }}
        />
        
        <motion.div 
          className="absolute top-0 left-10 w-72 h-72 bg-secondary/5 rounded-full blur-3xl"
          animate={{ 
            x: [0, -20],
            y: [0, 20]
          }}
          transition={{ 
            repeat: Infinity,
            repeatType: "reverse",
            duration: 18,
            ease: "easeInOut" 
          }}
        />
      </div>

      {/* Japanese pattern background */}
      <div className="absolute inset-0 overflow-hidden opacity-5 pattern-japanese">
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="text-sm font-medium px-4 py-1.5 rounded-full bg-primary/10 text-primary inline-block mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            Program Intakes
          </motion.span>
          <h2 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-black via-black to-black/80 mb-6">
            Our <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">Intake</span> Schedule
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            Join our programs throughout the year with our quarterly intake schedule
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {intakes.map((intake, index) => (
            <motion.div 
              key={intake.month}
              className="rounded-xl overflow-hidden shadow-lg bg-white border border-gray-100 hover:shadow-xl transition-shadow relative group"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className={`h-28 bg-gradient-to-r ${index % 2 === 0 ? 'from-primary to-primary/80' : 'from-secondary to-secondary/80'} flex items-center justify-center relative overflow-hidden`}>
                <motion.div 
                  className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.7 }}
                />
                <motion.div 
                  className="text-white bg-white/10 p-4 rounded-full"
                  animate={{ 
                    scale: [1, 1.05, 1],
                    rotate: [0, 2, 0, -2, 0], 
                  }}
                  transition={{ 
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: index * 0.2
                  }}
                >
                  {intake.icon}
                </motion.div>
              </div>
              
              <div className="p-6 text-center">
                <h3 className="text-2xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">{intake.month}</h3>
                <p className="text-gray-600">{intake.description}</p>
                
                <motion.div 
                  className="mt-6"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <RouterLink to="/apply" onClick={() => handleApplyClick(intake.month)}>
                    <motion.button 
                      className="inline-block px-5 py-2.5 bg-gradient-to-r from-primary to-secondary text-white rounded-full font-medium relative overflow-hidden group"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span className="relative z-10">Apply Now</span>
                      <motion.span 
                        className="absolute inset-0 bg-gradient-to-r from-primary via-secondary to-primary"
                        initial={{ x: "-100%" }}
                        whileHover={{ x: "100%" }}
                        transition={{ duration: 1 }}
                      />
                    </motion.button>
                  </RouterLink>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Intakes; 